import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Link2, Copy, Download, Plus, Edit, Trash2, BarChart3,
  ChevronLeft, ChevronRight, Check, RefreshCw, Home, Infinity as InfinityIcon,
  Network, ToggleLeft, ToggleRight,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import AdminMenu from "../components/AdminMenu";
import MainMenu from "../components/MainMenu";
import CoinBalancePill from "../components/CoinBalancePill";
import AffiliatePayoutsPanel from "../components/AffiliatePayoutsPanel";
import AffiliateDownlineTimeline from "../components/AffiliateDownlineTimeline";
import BalanceCard from "../components/affiliate/BalanceCard";
import QualificationCard from "../components/affiliate/QualificationCard";
import ReferralsList from "../components/affiliate/ReferralsList";
import CommissionsTab from "../components/affiliate/CommissionsTab";
import AffiliateLinkCard from "../components/affiliate/AffiliateLinkCard";
import AuditPerUser from "./audit/AuditPerUser";

const VALID_TABS = ["overview", "referrals", "commissions", "audit"];

const AffiliatePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTheme } = useTheme();
  const isCyber = currentTheme === "cyber_agent";

  const initialTab = (() => {
    const t = searchParams.get("tab");
    return VALID_TABS.includes(t) ? t : "overview";
  })();
  const [tab, setTab] = useState(initialTab);

  // Keep tab in sync when the URL changes (e.g. navigate from another page
  // hitting /affiliate?tab=audit — without this the initial useState only
  // fires on mount and clicking cross-page links wouldn't switch tabs).
  useEffect(() => {
    const t = searchParams.get("tab");
    if (VALID_TABS.includes(t) && t !== tab) {
      setTab(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ── V2 data ─────────────────────────────────────────────────────────────
  const [balances, setBalances] = useState(null);
  const [qualification, setQualification] = useState(null);

  // ── Link + welcome message ──────────────────────────────────────────────
  const [linkData, setLinkData] = useState(null);
  const [customLink, setCustomLink] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const [savingMsg, setSavingMsg] = useState(false);

  // ── Affiliate coin account ──────────────────────────────────────────────
  const [affAccount, setAffAccount] = useState(null);
  const [transferAmt, setTransferAmt] = useState("");
  const [transferDir, setTransferDir] = useState("to_affiliate");
  const [transferring, setTransferring] = useState(false);
  const [autoTopupPct, setAutoTopupPct] = useState(0);
  const [savingTopup, setSavingTopup] = useState(false);

  // ── Codes ───────────────────────────────────────────────────────────────
  const [codes, setCodes] = useState([]);
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [newCode, setNewCode] = useState({
    code: "", code_type: "join", bonus_tokens: 0, max_uses: -1, welcome_message: "",
  });
  const [creatingCode, setCreatingCode] = useState(false);
  const [editingCode, setEditingCode] = useState(null);

  // ── Analytics ───────────────────────────────────────────────────────────
  const [analyticsModal, setAnalyticsModal] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPage, setAnalyticsPage] = useState(1);
  const [analyticsDateRange, setAnalyticsDateRange] = useState("all");

  const [loading, setLoading] = useState(true);

  const accent = isCyber ? "cyan-400" : "divine-gold";
  const accentBg = isCyber ? "bg-cyan-500" : "bg-divine-gold";
  const accentText = isCyber ? "text-cyan-400" : "text-divine-gold";
  const accentBorder = isCyber ? "border-cyan-500/30" : "border-divine-gold/30";
  const cardBg = isCyber ? "bg-[#0a0a0f] border-cyan-500/20" : "bg-white/5 border-white/10";

  // ── Fetches ─────────────────────────────────────────────────────────────
  const fetchBalance = useCallback(async () => {
    try {
      const res = await authAxios.get("/affiliate/balance");
      setBalances(res.data);
    } catch (e) { console.debug("balance fetch:", e); }
  }, []);

  const fetchQualification = useCallback(async () => {
    try {
      const res = await authAxios.get("/affiliate/qualification");
      setQualification(res.data);
    } catch (e) { console.debug("qualification fetch:", e); }
  }, []);

  const fetchLink = useCallback(async () => {
    try {
      const res = await authAxios.get("/affiliate/link");
      setLinkData(res.data);
      setCustomLink(res.data.link || "");
      setWelcomeMsg(res.data.welcome_message || "");
    } catch (e) { console.debug("Link fetch:", e); }
  }, []);

  const fetchAffAccount = useCallback(async () => {
    try {
      const res = await authAxios.get("/affiliate/account");
      setAffAccount(res.data);
      setAutoTopupPct(res.data.auto_topup_pct || 0);
    } catch (e) { console.debug("Aff account:", e); }
  }, []);

  const fetchCodes = useCallback(async () => {
    try {
      const res = await authAxios.get("/affiliate/codes");
      setCodes(res.data.codes);
    } catch (e) { console.debug("Codes fetch:", e); }
  }, []);

  useEffect(() => {
    Promise.all([
      fetchBalance(), fetchQualification(),
      fetchLink(), fetchCodes(), fetchAffAccount(),
    ]).finally(() => setLoading(false));
  }, [fetchBalance, fetchQualification, fetchLink, fetchCodes, fetchAffAccount]);

  // Keep URL synced with tab for back/forward friendliness
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (tab === "overview") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const copyLink = () => {
    if (customLink) {
      navigator.clipboard.writeText(customLink);
      toast.success("Affiliate link copied!");
    }
  };

  const saveLink = async () => {
    setSavingLink(true);
    setLinkError("");
    try {
      await authAxios.put("/affiliate/link", { link: customLink });
      await fetchLink();
      toast.success("Link updated & QR refreshed!");
    } catch (e) {
      setLinkError(e.response?.data?.detail || "Invalid link");
    }
    setSavingLink(false);
  };

  const saveWelcomeMessage = async () => {
    setSavingMsg(true);
    try {
      await authAxios.put("/affiliate/welcome-message", { welcome_message: welcomeMsg });
      toast.success("Welcome message saved!");
    } catch (e) { toast.error("Failed to save message"); }
    finally { setSavingMsg(false); }
  };

  const downloadQR = () => {
    if (!linkData?.qr_hires) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${linkData.qr_hires}`;
    link.download = `AgentEros_QR_${linkData.aff_id}.png`;
    link.click();
    toast.success("QR code downloaded!");
  };

  const handleTransfer = async () => {
    const amt = parseInt(transferAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setTransferring(true);
    try {
      await authAxios.post("/affiliate/account/transfer", { amount: amt, direction: transferDir });
      toast.success(`Transferred ${amt.toLocaleString()} coins`);
      setTransferAmt("");
      await fetchAffAccount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    }
    setTransferring(false);
  };

  const handleSaveTopup = async () => {
    setSavingTopup(true);
    try {
      await authAxios.put("/affiliate/account/auto-topup", { percentage: autoTopupPct });
      toast.success("Auto topup updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
    setSavingTopup(false);
  };

  const handleCreateCode = async () => {
    // SUBSCRIBE offers don't need a code — they auto-apply on /plans for downlines.
    // Only require a code for join/gift codes.
    if (newCode.code_type !== "subscribe" && !newCode.code.trim()) {
      toast.error("Code is required"); return;
    }
    setCreatingCode(true);
    try {
      await authAxios.post("/affiliate/codes", newCode);
      toast.success("Code created!");
      setShowCreateCode(false);
      setNewCode({ code: "", code_type: "join", bonus_tokens: 0, max_uses: -1, welcome_message: "" });
      fetchCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create code");
    } finally { setCreatingCode(false); }
  };

  const handleUpdateCode = async () => {
    if (!editingCode) return;
    try {
      await authAxios.put(`/affiliate/codes/${editingCode.code_id}`, {
        code_type: editingCode.code_type || "join",
        bonus_tokens: editingCode.bonus_tokens,
        max_uses: editingCode.max_uses,
        welcome_message: editingCode.welcome_message,
        is_active: editingCode.is_active,
      });
      toast.success("Code updated!");
      setEditingCode(null);
      fetchCodes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update code"); }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm("Delete this code?")) return;
    try {
      await authAxios.delete(`/affiliate/codes/${codeId}`);
      toast.success("Code deleted!");
      fetchCodes();
    } catch (e) { toast.error("Failed to delete code"); }
  };

  const toggleCodeActive = async (code) => {
    try {
      await authAxios.put(`/affiliate/codes/${code.code_id}`, { is_active: !code.is_active });
      fetchCodes();
    } catch (e) { toast.error("Failed to toggle code"); }
  };

  const openAnalytics = async (code) => {
    setAnalyticsModal(code);
    setAnalyticsPage(1);
    setAnalyticsDateRange("all");
    await fetchAnalytics(code.code_id, 1, "all");
  };

  const fetchAnalytics = async (codeId, page = 1, range = "all") => {
    try {
      let params = `page=${page}&size=20`;
      if (range !== "all") {
        const end = new Date().toISOString();
        const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
        const start = new Date(Date.now() - days * 86400000).toISOString();
        params += `&start=${start}&end=${end}`;
      }
      const res = await authAxios.get(`/affiliate/codes/${codeId}/analytics?${params}`);
      setAnalyticsData(res.data);
    } catch (e) { toast.error("Failed to load analytics"); }
  };

  const isExhausted = (c) => c.max_uses !== -1 && c.use_count >= c.max_uses;
  const isPro = qualification?.compensation_profile === "PRO";

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <RefreshCw className={`w-8 h-8 ${accentText} animate-spin`} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-divine-black text-white">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="border-b border-white/10 bg-divine-black-light sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-white/60 hover:text-white"
              data-testid="affiliate-back-btn"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                alt="Agent Eros"
                className="w-6 h-6 object-contain"
                style={{ animation: isCyber ? "eros-glow-cyan 3s ease-in-out infinite" : "eros-glow-gold 3s ease-in-out infinite" }}
              />
            </Button>
            <h1 className={`text-lg ${isCyber ? "text-white font-semibold" : "font-cinzel text-white"}`}>
              Affiliate Dashboard
            </h1>
            {isPro && (
              <span
                data-testid="pro-header-badge"
                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#C5A55A]/40 bg-[#C5A55A]/10 text-[#C5A55A] text-[10px] font-semibold uppercase tracking-wider"
              >
                PRO
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <CoinBalancePill />
            <AdminMenu />
            <MainMenu exclude="/affiliate" />
          </div>
        </div>
      </div>

      {/* Tabs — /profile-style horizontal sticky submenu.
          On mobile the tab strip scrolls horizontally, edge-bleed under the
          6-unit page padding so it feels flush with the viewport. On md+
          it centres inside a bordered container. */}
      <div className="max-w-6xl mx-auto px-6 pt-0.5 md:pt-6 pb-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="sticky top-[60px] z-40 flex md:grid w-auto md:w-full md:max-w-4xl md:grid-cols-4 md:justify-center justify-start bg-divine-charcoal border-y border-divine-gold/20 overflow-x-auto md:overflow-visible whitespace-nowrap rounded-none -mx-6 mb-6 pl-4 pr-4 md:mx-0 md:pl-1 md:pr-1 md:rounded-md md:border md:sticky md:top-[66px] touch-pan-x overscroll-x-contain [will-change:transform]">
            <TabsTrigger
              value="overview"
              data-testid="tab-overview"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="referrals"
              data-testid="tab-referrals"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              Referrals
            </TabsTrigger>
            <TabsTrigger
              value="commissions"
              data-testid="tab-commissions"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              Commissions
            </TabsTrigger>
            <TabsTrigger
              value="audit"
              data-testid="tab-audit"
              className="data-[state=active]:bg-divine-gold data-[state=active]:text-divine-black"
            >
              Audit
            </TabsTrigger>
          </TabsList>

          {/* ============================ OVERVIEW ============================ */}
          <TabsContent value="overview" className="mt-6 space-y-6" data-testid="overview-panel">
            <BalanceCard
              balances={balances}
              minimumCashCents={balances?.minimum_cash_payout_cents}
            />
            <QualificationCard qualification={qualification} onRefresh={setQualification} />
            <AffiliatePayoutsPanel />
          </TabsContent>

          {/* ============================ AUDIT (self) ============================ */}
          <TabsContent value="audit" className="mt-6" data-testid="audit-panel">
            <div className="-mx-6">
              <AuditPerUser selfMode={true} />
            </div>
          </TabsContent>

          {/* ============================ REFERRALS ============================ */}
          <TabsContent value="referrals" className="mt-6 space-y-8" data-testid="referrals-panel">
            <ReferralsList />
            <AffiliateDownlineTimeline />

            {/* Bonus Coin Codes */}
            <section data-testid="affiliate-codes-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className={`text-lg font-semibold ${accentText}`}>Bonus Coin Codes</h2>
                <Button
                  onClick={() => setShowCreateCode(true)}
                  size="sm"
                  className={`${accentBg} text-black`}
                  data-testid="create-code-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> New Code
                </Button>
              </div>
              <p className="text-xs text-white/40 mb-4 leading-relaxed">
                Optional promo codes you can attach to your affiliate link so new sign-ups land
                with bonus coins. Coins come out of your Affiliate Coin Account below.
              </p>

              {showCreateCode && (
                <div className={`rounded-xl border p-5 mb-4 ${cardBg}`} data-testid="create-code-form">
                  <h3 className="text-sm font-semibold mb-3">Create New Code</h3>
                  <div className="mb-4">
                    <Label className="text-xs text-white/60 mb-2 block">Code Type</Label>
                    <div className="space-y-2">
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          newCode.code_type === "join"
                            ? `${accentBorder} bg-white/5`
                            : "border-white/10 hover:border-white/20"
                        }`}
                        data-testid="code-type-join"
                      >
                        <input
                          type="radio" name="code_type" value="join"
                          checked={newCode.code_type === "join"}
                          onChange={() => setNewCode({ ...newCode, code_type: "join" })}
                          className="mt-0.5 accent-[#C5A55A]"
                        />
                        <div>
                          <span className="text-sm text-white/80 font-medium">Join</span>
                          <p className="text-xs text-white/40 mt-0.5">
                            Bonus applied when a new user joins (coins can be 0 for tracking only).
                          </p>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          newCode.code_type === "subscribe"
                            ? `${accentBorder} bg-white/5`
                            : "border-white/10 hover:border-white/20"
                        }`}
                        data-testid="code-type-subscribe"
                      >
                        <input
                          type="radio" name="code_type" value="subscribe"
                          checked={newCode.code_type === "subscribe"}
                          onChange={() => setNewCode({ ...newCode, code_type: "subscribe" })}
                          className="mt-0.5 accent-[#C5A55A]"
                        />
                        <div>
                          <span className="text-sm text-white/80 font-medium">Subscribe</span>
                          <p className="text-xs text-white/40 mt-0.5">
                            100% bonus on top plan; 10-50% on lesser plans.
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {newCode.code_type !== "subscribe" && (
                      <div>
                        <Label className="text-xs text-white/60">Code (4-20 chars)</Label>
                        <Input
                          value={newCode.code}
                          onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                          placeholder="e.g. EROS2026"
                          className="mt-1 bg-white/5 border-white/10 text-white"
                          data-testid="new-code-input"
                        />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-white/60">
                        {newCode.code_type === "subscribe" ? "Platinum-tier bonus (base)" : "Bonus Tokens"}
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={newCode.bonus_tokens}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") { setNewCode({ ...newCode, bonus_tokens: "" }); }
                          else if (/^\d+$/.test(raw)) { setNewCode({ ...newCode, bonus_tokens: parseInt(raw, 10) }); }
                        }}
                        onBlur={(e) => {
                          const n = parseInt(e.target.value, 10);
                          setNewCode({ ...newCode, bonus_tokens: Number.isFinite(n) && n > 0 ? n : 0 });
                        }}
                        className="mt-1 bg-white/5 border-white/10 text-white"
                        data-testid="new-code-tokens"
                      />
                      {newCode.code_type === "subscribe" && newCode.bonus_tokens > 0 && (
                        <>
                          <div className="mt-2 grid grid-cols-4 gap-1 text-[10px]">
                            {[
                              ["Bronze",   Math.floor(newCode.bonus_tokens * 10 / 75)],
                              ["Silver",   Math.floor(newCode.bonus_tokens * 25 / 75)],
                              ["Gold",     Math.floor(newCode.bonus_tokens * 50 / 75)],
                              ["Platinum", newCode.bonus_tokens],
                            ].map(([tier, val]) => (
                              <div key={tier} className="bg-white/5 rounded px-1.5 py-1 text-center">
                                <div className="text-white/40 uppercase tracking-wider">{tier}</div>
                                <div className="text-[#C5A55A] font-mono">{val.toLocaleString()}</div>
                              </div>
                            ))}
                          </div>
                          {(affAccount?.affiliate_balance || 0) < newCode.bonus_tokens && (
                            <p className="mt-2 text-[11px] text-red-400" data-testid="new-code-balance-warning">
                              This exceeds your Affiliate Code Balance ({(affAccount?.affiliate_balance || 0).toLocaleString()}) for even 1 use. Fund your account or lower the bonus.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Max Uses (-1 = unlimited)</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={newCode.max_uses}
                        onChange={(e) => {
                          // Free-form text entry — accept "-", "-1", any digits.
                          // Only parse to int on blur / submit; empty defaults to -1.
                          const raw = e.target.value;
                          if (raw === "" || raw === "-") {
                            setNewCode({ ...newCode, max_uses: raw });
                          } else if (/^-?\d+$/.test(raw)) {
                            setNewCode({ ...newCode, max_uses: parseInt(raw, 10) });
                          }
                        }}
                        onBlur={(e) => {
                          const raw = e.target.value;
                          if (raw === "" || raw === "-" || Number.isNaN(parseInt(raw, 10))) {
                            setNewCode({ ...newCode, max_uses: -1 });
                          }
                        }}
                        className="mt-1 bg-white/5 border-white/10 text-white"
                        data-testid="new-code-max-uses"
                      />
                      {newCode.code_type === "subscribe" && (
                        <p className="text-[10px] text-white/40 mt-1">
                          Leave -1 for auto (Affiliate Code Balance ÷ Platinum bonus). Enter a number to cap manually.
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Welcome Message</Label>
                      <Input
                        value={newCode.welcome_message}
                        onChange={(e) => setNewCode({ ...newCode, welcome_message: e.target.value.slice(0, 500) })}
                        placeholder="Optional"
                        className="mt-1 bg-white/5 border-white/10 text-white"
                        data-testid="new-code-welcome"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={handleCreateCode}
                      disabled={creatingCode}
                      size="sm"
                      className={`${accentBg} text-black`}
                      data-testid="submit-create-code-btn"
                    >
                      {creatingCode ? (
                        <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Plus className="w-3 h-3 mr-1" />
                      )}
                      Create
                    </Button>
                    <Button
                      onClick={() => setShowCreateCode(false)}
                      variant="ghost" size="sm" className="text-white/60"
                    >Cancel</Button>
                  </div>
                </div>
              )}

              {codes.length === 0 ? (
                <div className={`rounded-xl border p-8 text-center ${cardBg}`}>
                  <Link2 className="w-10 h-10 mx-auto mb-3 text-white/20" />
                  <p className="text-white/40">No codes yet. Create one to bootstrap sign-ups.</p>
                </div>
              ) : (
                <div className={`rounded-xl border overflow-hidden ${cardBg}`}>
                  {/* Subscribe offer explainer — floats above the codes table */}
                  <div className="px-4 py-2.5 bg-violet-500/[0.06] border-b border-violet-500/20 text-xs text-white/70">
                    <span className="text-violet-300 font-medium">"Subscribe" Bonus Offers</span>{" "}
                    are used to incentivise subscriptions with a limited number of subscribe-now offers to your referrals.
                    The bonus scales per plan (Platinum full · Gold 67% · Silver 33% · Bronze 13%).
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left px-4 py-3 text-white/60">Code</th>
                          <th className="text-center px-4 py-3 text-white/60">Type</th>
                          <th className="text-center px-4 py-3 text-white/60">Bonus</th>
                          <th className="text-center px-4 py-3 text-white/60">Uses</th>
                          <th className="text-left px-4 py-3 text-white/60">Welcome</th>
                          <th className="text-center px-4 py-3 text-white/60">Status</th>
                          <th className="text-center px-4 py-3 text-white/60">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...codes]
                          .sort((a, b) => {
                            // Subscribe codes float to the top; then by created_at desc.
                            if (a.code_type === "subscribe" && b.code_type !== "subscribe") return -1;
                            if (b.code_type === "subscribe" && a.code_type !== "subscribe") return 1;
                            return (b.created_at || "").localeCompare(a.created_at || "");
                          })
                          .map((code) => (
                          <tr key={code.code_id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="px-4 py-3 font-mono font-semibold">
                              {code.code_type === "subscribe" ? (
                                <span className="text-white/40 italic text-xs">— auto —</span>
                              ) : (
                                code.code
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  code.code_type === "subscribe"
                                    ? "bg-violet-500/20 text-violet-400"
                                    : "bg-cyan-500/20 text-cyan-400"
                                }`}
                              >
                                {code.code_type === "subscribe" ? "Subscribe" : "Join"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">{code.bonus_tokens}</td>
                            <td className="px-4 py-3 text-center">
                              {code.use_count} /{" "}
                              {code.max_uses === -1 ? (
                                <InfinityIcon className="w-4 h-4 inline text-white/40" />
                              ) : (
                                code.max_uses
                              )}
                            </td>
                            <td className="px-4 py-3 text-white/50 max-w-[150px] truncate">
                              {code.welcome_message || "-"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {isExhausted(code) && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400">
                                    Exhausted
                                  </span>
                                )}
                                <span
                                  className={`text-xs px-2 py-0.5 rounded-full ${
                                    code.is_active
                                      ? "bg-green-500/20 text-green-400"
                                      : "bg-red-500/20 text-red-400"
                                  }`}
                                >
                                  {code.is_active ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => setEditingCode({ ...code })}
                                  className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white"
                                  data-testid={`edit-code-${code.code}`}
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => toggleCodeActive(code)}
                                  className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white"
                                  data-testid={`toggle-code-${code.code}`}
                                >
                                  {code.is_active ? (
                                    <ToggleRight className="w-3.5 h-3.5 text-green-400" />
                                  ) : (
                                    <ToggleLeft className="w-3.5 h-3.5 text-red-400" />
                                  )}
                                </button>
                                <button
                                  onClick={() => openAnalytics(code)}
                                  className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white"
                                  data-testid={`analytics-code-${code.code}`}
                                >
                                  <BarChart3 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteCode(code.code_id)}
                                  className="p-1.5 rounded hover:bg-white/10 text-red-400/60 hover:text-red-400"
                                  data-testid={`delete-code-${code.code}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>

            {/* Affiliate Link + QR (moved from Overview per user request) */}
            <AffiliateLinkCard
              linkData={linkData}
              customLink={customLink}
              setCustomLink={setCustomLink}
              welcomeMsg={welcomeMsg}
              setWelcomeMsg={setWelcomeMsg}
              savingLink={savingLink}
              savingMsg={savingMsg}
              linkError={linkError}
              setLinkError={setLinkError}
              saveLink={saveLink}
              saveWelcomeMessage={saveWelcomeMessage}
              copyLink={copyLink}
              downloadQR={downloadQR}
              accentBg={accentBg}
              accentText={accentText}
              accentBorder={accentBorder}
              cardBg={cardBg}
            />

            {/* Affiliate Coin Account */}
            <section data-testid="affiliate-coin-account-section">
              <h2 className={`text-lg font-semibold mb-4 ${accentText}`}>Affiliate Coin Account</h2>
              <div className={`rounded-xl border p-6 ${cardBg}`}>
                <p className="text-xs text-white/40 mb-4">
                  Funds your bonus coin codes above. Transfer coins in from your main balance
                  or set an auto top-up percentage of your subscription coins.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <div className="rounded-lg border border-white/10 p-4 bg-white/[0.02]">
                    <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Available Balance</p>
                    <p className="text-2xl font-bold font-mono text-white/70" data-testid="main-coin-balance">
                      {(affAccount?.transferable_balance || 0).toLocaleString()}
                    </p>
                    <p className="text-[10px] text-white/40 mt-1" data-testid="transferable-hint">
                      Bonus Coin balance (not transferable){" "}
                      <span className="text-white/60 font-mono">{(affAccount?.bonus_balance || 0).toLocaleString()}</span>
                    </p>
                  </div>
                  <div className={`rounded-lg border p-4 ${accentBorder} bg-white/[0.02]`}>
                    <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Affiliate Code Balance</p>
                    <p className={`text-2xl font-bold font-mono ${accentText}`} data-testid="aff-coin-balance">
                      {(affAccount?.affiliate_balance || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-3 mb-3 pb-5 border-b border-white/[0.06]">
                  <div>
                    <Label className="text-xs text-white/60 mb-1 block">Amount</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={transferAmt}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "" || /^\d+$/.test(raw)) setTransferAmt(raw);
                      }}
                      placeholder="0"
                      className="w-32 bg-white/5 border-white/10 text-white"
                      data-testid="transfer-amount-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60 mb-1 block">Direction</Label>
                    <select
                      value={transferDir}
                      onChange={(e) => setTransferDir(e.target.value)}
                      className={`h-9 px-3 rounded-md bg-[#0a0a0a] border ${accentBorder} ${accentText} text-sm`}
                      data-testid="transfer-direction-select"
                    >
                      <option value="to_affiliate">Main → Affiliate</option>
                      <option value="to_main">Main ← Affiliate</option>
                    </select>
                  </div>
                  <Button
                    onClick={handleTransfer}
                    disabled={
                      transferring ||
                      (transferDir === "to_affiliate" &&
                        parseInt(transferAmt) > (affAccount?.transferable_balance || 0))
                    }
                    size="sm"
                    className={`${accentBg} text-black`}
                    data-testid="transfer-btn"
                  >
                    {transferring ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null}
                    Transfer
                  </Button>
                </div>
                {transferDir === "to_affiliate" &&
                  parseInt(transferAmt) > (affAccount?.transferable_balance || 0) &&
                  parseInt(transferAmt) > 0 && (
                  <p className="text-xs text-amber-400 mb-3" data-testid="transfer-cap-warning">
                    Amount exceeds transferable coins ({(affAccount?.transferable_balance || 0).toLocaleString()}).
                    Bonus coins remain on Main for in-app use.
                  </p>
                )}

                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label className="text-xs text-white/60 mb-1 block">Auto Topup Affiliate Code Balance from Subscription</Label>
                    <p className="text-[11px] text-white/25 mb-1.5">
                      Automatically transfer % of subscription coins into your Affiliate Code Coins Account to keep your bonus codes and subscription codes coin payouts active
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={autoTopupPct}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") { setAutoTopupPct(""); }
                          else if (/^\d+$/.test(raw)) {
                            setAutoTopupPct(Math.min(100, parseInt(raw, 10)));
                          }
                        }}
                        onBlur={(e) => {
                          const n = parseInt(e.target.value, 10);
                          setAutoTopupPct(Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0);
                        }}
                        className="w-20 bg-white/5 border-white/10 text-white"
                        data-testid="auto-topup-input"
                      />
                      <span className="text-white/40 text-sm">%</span>
                      <Button
                        onClick={handleSaveTopup}
                        disabled={savingTopup}
                        size="sm" variant="outline"
                        className={`text-xs ${accentBorder} ${accentText}`}
                        data-testid="save-topup-btn"
                      >
                        {savingTopup ? "Saving..." : "Save"}
                      </Button>
                    </div>
                    {autoTopupPct > 0 && (affAccount?.next_topup_estimate || 0) > 0 && (
                      <p className="text-[11px] text-white/40 mt-2" data-testid="next-topup-preview">
                        Next drip will auto-move ~<span className="text-white/70 font-mono">{(affAccount.next_topup_estimate || 0).toLocaleString()}</span> coins into your Affiliate Code Balance.
                      </p>
                    )}
                    {affAccount && !affAccount.has_subscription && (
                      <div
                        data-testid="no-subscription-cta"
                        className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-white/50"
                      >
                        <span>
                          Auto affiliate code topups activate on subscription coin drops.
                        </span>
                        <Button
                          onClick={() => navigate("/plans", { state: { from: "/affiliate" } })}
                          size="sm"
                          variant="outline"
                          className={`h-6 px-2 text-[11px] ${accentBorder} ${accentText}`}
                          data-testid="activate-subscription-cta"
                        >
                          Activate Subscription
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </TabsContent>

          {/* ============================ COMMISSIONS ============================ */}
          <TabsContent value="commissions" className="mt-6" data-testid="commissions-panel">
            <CommissionsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Code Modal */}
      <Dialog open={!!editingCode} onOpenChange={(o) => !o && setEditingCode(null)}>
        <DialogContent className={isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-charcoal border-divine-gold/30"}>
          <DialogHeader>
            <DialogTitle className={accentText}>Edit Code: {editingCode?.code}</DialogTitle>
          </DialogHeader>
          {editingCode && (
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs text-white/60 mb-2 block">Code Type</Label>
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${(editingCode.code_type || "join") === "join" ? `${accentBorder} bg-white/5` : "border-white/10 hover:border-white/20"}`}>
                    <input
                      type="radio" name="edit_code_type" value="join"
                      checked={(editingCode.code_type || "join") === "join"}
                      onChange={() => setEditingCode({ ...editingCode, code_type: "join" })}
                      className="mt-0.5 accent-[#C5A55A]"
                    />
                    <div>
                      <span className="text-sm text-white/80 font-medium">Join</span>
                      <p className="text-xs text-white/40 mt-0.5">Bonus on new user join</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${editingCode.code_type === "subscribe" ? `${accentBorder} bg-white/5` : "border-white/10 hover:border-white/20"}`}>
                    <input
                      type="radio" name="edit_code_type" value="subscribe"
                      checked={editingCode.code_type === "subscribe"}
                      onChange={() => setEditingCode({ ...editingCode, code_type: "subscribe" })}
                      className="mt-0.5 accent-[#C5A55A]"
                    />
                    <div>
                      <span className="text-sm text-white/80 font-medium">Subscribe</span>
                      <p className="text-xs text-white/40 mt-0.5">100% bonus on top plan</p>
                    </div>
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs text-white/60">
                  {editingCode.code_type === "subscribe" ? "Platinum-tier bonus (base)" : "Bonus Tokens"}
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={editingCode.bonus_tokens}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") { setEditingCode({ ...editingCode, bonus_tokens: "" }); }
                    else if (/^\d+$/.test(raw)) { setEditingCode({ ...editingCode, bonus_tokens: parseInt(raw, 10) }); }
                  }}
                  onBlur={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setEditingCode({ ...editingCode, bonus_tokens: Number.isFinite(n) && n > 0 ? n : 0 });
                  }}
                  className="mt-1 bg-white/5 border-white/10 text-white"
                />
                {editingCode.code_type === "subscribe" && editingCode.bonus_tokens > 0 && (
                  <>
                    <div className="mt-2 grid grid-cols-4 gap-1 text-[10px]">
                      {[
                        ["Bronze",   Math.floor(editingCode.bonus_tokens * 10 / 75)],
                        ["Silver",   Math.floor(editingCode.bonus_tokens * 25 / 75)],
                        ["Gold",     Math.floor(editingCode.bonus_tokens * 50 / 75)],
                        ["Platinum", editingCode.bonus_tokens],
                      ].map(([tier, val]) => (
                        <div key={tier} className="bg-white/5 rounded px-1.5 py-1 text-center">
                          <div className="text-white/40 uppercase tracking-wider">{tier}</div>
                          <div className="text-[#C5A55A] font-mono">{val.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                    {(affAccount?.affiliate_balance || 0) < editingCode.bonus_tokens && (
                      <p className="mt-2 text-[11px] text-red-400" data-testid="edit-code-balance-warning">
                        This exceeds your Affiliate Code Balance ({(affAccount?.affiliate_balance || 0).toLocaleString()}) for even 1 use. Fund your account or lower the bonus.
                      </p>
                    )}
                  </>
                )}
              </div>
              <div>
                <Label className="text-xs text-white/60">Max Uses (-1 = unlimited)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={editingCode.max_uses}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "" || raw === "-") {
                      setEditingCode({ ...editingCode, max_uses: raw });
                    } else if (/^-?\d+$/.test(raw)) {
                      setEditingCode({ ...editingCode, max_uses: parseInt(raw, 10) });
                    }
                  }}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    if (raw === "" || raw === "-" || Number.isNaN(parseInt(raw, 10))) {
                      setEditingCode({ ...editingCode, max_uses: -1 });
                    }
                  }}
                  className="mt-1 bg-white/5 border-white/10 text-white"
                />
                {editingCode.code_type === "subscribe" && (
                  <p className="text-[10px] text-white/40 mt-1">
                    Leave -1 for auto (Affiliate Code Balance ÷ Platinum bonus).
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs text-white/60">Welcome Message</Label>
                <Textarea
                  value={editingCode.welcome_message || ""}
                  onChange={(e) => setEditingCode({ ...editingCode, welcome_message: e.target.value.slice(0, 500) })}
                  className="mt-1 bg-white/5 border-white/10 text-white min-h-[60px]"
                />
                <span className="text-xs text-white/30">
                  {(editingCode.welcome_message || "").length}/500
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-white/60">Active</Label>
                <button
                  onClick={() => setEditingCode({ ...editingCode, is_active: !editingCode.is_active })}
                  className="text-white/60 hover:text-white"
                >
                  {editingCode.is_active ? (
                    <ToggleRight className="w-6 h-6 text-green-400" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-red-400" />
                  )}
                </button>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleUpdateCode} className={`${accentBg} text-black`}>
                  Save Changes
                </Button>
                <Button variant="ghost" onClick={() => setEditingCode(null)} className="text-white/60">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Analytics Modal */}
      <Dialog
        open={!!analyticsModal}
        onOpenChange={(o) => { if (!o) { setAnalyticsModal(null); setAnalyticsData(null); } }}
      >
        <DialogContent className={`${isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-charcoal border-divine-gold/30"} max-w-2xl max-h-[85vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className={accentText}>
              <BarChart3 className="w-5 h-5 inline mr-2" />
              Analytics: {analyticsModal?.code}
            </DialogTitle>
          </DialogHeader>
          {analyticsModal && analyticsData && (
            <div className="space-y-4 mt-2">
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="text-white/60">
                  Total Uses:{" "}
                  <strong className="text-white">{analyticsData.code?.use_count}</strong> /{" "}
                  {analyticsData.code?.max_uses === -1 ? (
                    <InfinityIcon className="w-4 h-4 inline text-white/40" />
                  ) : (
                    analyticsData.code?.max_uses
                  )}
                </span>
                <span className="text-white/60">
                  Status:{" "}
                  <strong className={analyticsData.code?.is_active ? "text-green-400" : "text-red-400"}>
                    {analyticsData.code?.is_active ? "Active" : "Inactive"}
                  </strong>
                </span>
              </div>
              <div className="flex gap-2">
                {["7d", "30d", "90d", "all"].map((r) => (
                  <Button
                    key={r} size="sm"
                    variant={analyticsDateRange === r ? "default" : "ghost"}
                    className={analyticsDateRange === r ? `${accentBg} text-black` : "text-white/50"}
                    onClick={() => {
                      setAnalyticsDateRange(r);
                      setAnalyticsPage(1);
                      fetchAnalytics(analyticsModal.code_id, 1, r);
                    }}
                  >
                    {r === "all" ? "All Time" : `Last ${r.replace("d", "")} days`}
                  </Button>
                ))}
              </div>
              {analyticsData.chart_data?.length > 0 ? (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.chart_data}>
                      <XAxis dataKey="date" tick={{ fill: "#ffffff60", fontSize: 11 }} />
                      <YAxis tick={{ fill: "#ffffff60", fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8 }}
                        labelStyle={{ color: "#fff" }}
                        itemStyle={{ color: isCyber ? "#22d3ee" : "#d4a853" }}
                      />
                      <Bar dataKey="count" fill={isCyber ? "#22d3ee" : "#d4a853"} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-white/30 text-center py-4">
                  No redemptions in this period.
                </p>
              )}
              <div>
                <h4 className="text-sm font-semibold text-white/70 mb-2">Redemption Log</h4>
                {analyticsData.redemptions?.length > 0 ? (
                  <>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left px-3 py-2 text-white/60">User</th>
                          <th className="text-left px-3 py-2 text-white/60">Date</th>
                          <th className="text-center px-3 py-2 text-white/60">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.redemptions.map((r, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="px-3 py-2">{r.user}</td>
                            <td className="px-3 py-2 text-white/60">
                              {r.date?.slice(0, 16)?.replace("T", " ")}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  r.status === "Active"
                                    ? "bg-green-500/20 text-green-400"
                                    : "bg-red-500/20 text-red-400"
                                }`}
                              >
                                {r.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {analyticsData.pages > 1 && (
                      <div className="flex items-center justify-between mt-2 text-xs text-white/40">
                        <span>Page {analyticsData.page} of {analyticsData.pages}</span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost" size="sm"
                            disabled={analyticsPage <= 1}
                            onClick={() => {
                              setAnalyticsPage((p) => p - 1);
                              fetchAnalytics(analyticsModal.code_id, analyticsPage - 1, analyticsDateRange);
                            }}
                          >
                            <ChevronLeft className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            disabled={analyticsPage >= analyticsData.pages}
                            onClick={() => {
                              setAnalyticsPage((p) => p + 1);
                              fetchAnalytics(analyticsModal.code_id, analyticsPage + 1, analyticsDateRange);
                            }}
                          >
                            <ChevronRight className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-white/30 text-center py-3">
                    No redemptions recorded.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliatePage;
